import {
  Row,
  Col,
  InputGroup,
  Button,
  InputGroupText,
  Label,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import React from "react";
import { connect } from "react-redux";
import { firebaseConnect } from "react-redux-firebase";
import firebase from "firebase/compat/app";
import Dashboard, { isNewContactDonation } from "../../components/dashboard";
import { donationConsoleUrl } from "../../../utils/firebaseConsoleUrls";
import { resolveDonorsFirebaseProjectId } from "../../../utils/resolveDonorsFirebaseProjectId";
import {
  enqueueReindexOperation,
  paginateDonations,
  searchDonorById,
} from "../../../utils/fetch";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";
import DatePicker from "react-datepicker";
import datePickerS from "react-datepicker/dist/react-datepicker.css";
import { Moment } from "moment";
import moment from "moment";
import isEqual from "lodash.isequal";
import IDonation from "../../../types/services/IDonation";
import IPaginatedResponse from "../../../types/services/IPaginatedResponse";
import { ExportType } from "../exportModal";
import * as commonActions from "../../../utils/actions/common";
import ISelectionUpdate from "../../../types/ISelectionUpdate";
import {
  withModals,
  ModalProps,
  serviceCaller,
  ServiceCallerProps,
  withLoginRedirect,
} from "../../composition";
import get from "lodash.get";
import classNames from "classnames";
import ReactTooltip from "react-tooltip";
import type { ISortOption } from "../sortDropdown";
import IColumnName from "../../../types/IColumnName";
import {
  applyColumnPrefs,
  TableIds,
  toggleSavedColumn,
} from "../../../utils/tablePrefs";
import Fragment from "../../../toolkit/fragment";
import NotFoundPage from "../../../toolkit/notFound";
import StripeKeyRotationBanner from "./stripeKeyRotationBanner";
import { isCurrentUserOnlyCollaborator } from "../../../utils/roleCheck";
import {
  isDonorDeactivated,
  donorHasNotifications,
  getActiveMedium,
} from "../../../utils/helpers";
import {
  lookupViewSparkLabel,
  trackingPostIdOf,
} from "../../../utils/viewSparkLabel";
import { PersonalViewSparkType } from "../thankYouModal";

function formatReceivedPrettyShort(receivedOn: number): string {
  if (receivedOn == null) {
    return "";
  }
  return new Date(receivedOn).toLocaleDateString("en-US", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });
}


interface Props extends ServiceCallerProps, ModalProps {
  refreshDashboard: () => void;
  enableMotivationCode: boolean;
  customDonationUrl: string | null;
  needsToUpdateStripeKey: boolean;
  stripeAccountType: string;
}

interface State {
  actionsMenuOpen: boolean;
  sortBy: ISortOption;
  selectAll: boolean;
  sortAsc: boolean;
  startDate: Moment;
  endDate: Moment;
  selectedIds: string[];
  suppressedIds: string[];
  selectedData: IDonation[];
  totalResult: number;
  columnNames: Array<IColumnName>;
  columnData: Array<String>;
  donorsFirebaseProjectId?: string;
}

class DonationsPage extends React.PureComponent<Props, State> {
  private viewSparkLabelCache = new Map<string, string>();
  private viewSparkLabelCharityId: string | undefined;
  private lastDonationRows: IDonation[] = [];

  sortOptions: ISortOption[] = [
    { key: "RECEIVED", show: "Received On", colName: "receivedPretty" },
    { key: "ACCOUNT", show: "Contact #", colName: "donor.accountNumber" },
    { key: "AMOUNT", show: "Amount", colName: "amountPretty" },
    { key: "FREQUENCY", show: "Frequency", colName: "frequencyPretty" },
    { key: "CAMPAIGN", show: "Campaign", colName: "campaign" },
  ];

  getColumnData() {
    return [
      "donor.name",
      "amountPretty",
      "frequencyPretty",
      "receivedPretty",
      "campaign",
      "medium",
      "projectName",
      "viewSparkPretty",
    ];
  }

  getColumnNames() {
    return applyColumnPrefs(TableIds.donations, [
      { key: "Name", isDisabled: false },
      { key: "Amount", isDisabled: false },
      { key: "Frequency", isDisabled: false },
      { key: "Received On", isDisabled: false, hasCollapsingDate: true },
      { key: "Campaign", isDisabled: false },
      { key: "Medium", isDisabled: false },
      { key: "Project", isDisabled: false, hasCellTooltip: true },
      { key: "ViewSpark", isDisabled: false, hasCellTooltip: true },
    ]).map(column =>
      ["Project", "Medium"].includes(column.key)
        ? { ...column, isDisabled: false }
        : column
    );
  }

  renderDonationDetails = (donation: IDonation) => {
    const groups = [
      {
        title: "Contacts",
        fields: [
          ["Name", donation.donor?.name],
          ["Contact #", donation.donor?.accountNumber],
          ["Company", donation.donor?.company],
          ["Phone Number", donation.donor?.phoneNumber],
          ["Email", donation.donor?.email],
        ],
      },
      {
        title: "Payment",
        fields: [
          ["Amount", donation.amountPretty],
          ["Received On", donation.receivedPretty],
          [
            "Frequency",
            donation.frequency === 2 && donation.isFirst
              ? "Monthly (New!)"
              : donation.frequencyPretty,
          ],
          [
            "Payment",
            donation.payMethod
              ? donation.payMethod.charAt(0).toUpperCase() +
                donation.payMethod.slice(1)
              : "",
          ],
          ["Payment Token", donation.payMethodToken],
          ["Transaction ID", donation.transactionId],
        ],
      },
      {
        title: "Other Info",
        fields: [
          ["Campaign", donation.campaign],
          ["Project", get(donation, "projectName")],
          ["ViewSpark", donation.viewSparkPretty],
          [
            "Medium",
            donation.medium
              ? donation.medium.charAt(0).toUpperCase() +
                donation.medium.slice(1)
              : "",
          ],
          ["Notified To", donation.notifiedTo?.join(", ")],
          ...(this.props.enableMotivationCode
            ? [["Motivation Code", donation.motivationCode]]
            : []),
        ],
      },
    ];
    return (
      <div className={s.donationDetails}>
        {groups
          .map(group => ({
            ...group,
            fields: group.fields.filter(
              ([, value]) => value != null && String(value).trim() !== ""
            ),
          }))
          .filter(group => group.fields.length > 0)
          .map(group => (
            <section key={group.title}>
              <h3 className={s.detailsHeading}>{group.title}</h3>
              <dl className={s.detailsFields}>
                {group.fields.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>
                      <span
                        className={
                          label === "Amount" && donation.feesAssumed
                            ? s.coveredAmount
                            : undefined
                        }
                      >
                        {value}
                      </span>
                      {label === "Name" &&
                        isNewContactDonation(donation) && (
                          <span className={s.newContactNote}>
                            This contact was not in our records until receiving
                            this donation
                          </span>
                        )}
                      {label === "Amount" && donation.feesAssumed && (
                        <React.Fragment>
                          <span
                            className={s.feeCheck}
                            data-tip
                            data-for={`detail-fees-${donation.id}`}
                            tabIndex={0}
                            aria-label="Fees covered!"
                          >
                            <i className="fas fa-check" aria-hidden="true" />
                          </span>
                          <ReactTooltip
                            id={`detail-fees-${donation.id}`}
                            place="top"
                            effect="solid"
                          >
                            Fees covered!
                          </ReactTooltip>
                        </React.Fragment>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
      </div>
    );
  };

  state = {
    actionsMenuOpen: false,
    sortBy: this.sortOptions[0],
    selectAll: false,
    sortAsc: false,
    startDate: null,
    endDate: null,
    selectedIds: [],
    suppressedIds: [],
    selectedData: [],
    totalResult: 0,
    columnNames: this.getColumnNames(),
    columnData: this.getColumnData(),
  };

  componentDidMount() {
    if (window) {
      this.handleWindowSize();
      window.addEventListener("resize", this.handleWindowSize);
    }
    if (this.props.currentCharity) {
      if (isCurrentUserOnlyCollaborator(this.props.currentCharity)) {
        this.props.navigate("/viewsparks");
      }
      if (this.props.customDonationUrl) {
        this.props.history.push("/contacts");
      }
    }
    this.refreshDonorsFirebaseProjectId();
  }

  refreshDonorsFirebaseProjectId = async () => {
    if (!this.props.isAdminDebugUi) {
      return;
    }
    const charityId = this.props.currentCharity?.id;
    if (!charityId) {
      this.setState({ donorsFirebaseProjectId: undefined });
      return;
    }
    const projectId = await resolveDonorsFirebaseProjectId(charityId);
    this.setState({ donorsFirebaseProjectId: projectId });
  };

  componentWillUnmount() {
    if (window) {
      window.removeEventListener("resize", this.handleWindowSize);
    }
  }

  handleWindowSize = () => {
    this.setState({ columnNames: this.getColumnNames() }, () => {
      this.hideEmptyColumns(this.lastDonationRows);
    });
  };

  getResponse = (currentPage, pageSize) => {
    return paginateDonations(
      this.props.config.searchServiceUrl,
      this.props.currentCharity.id,
      currentPage,
      pageSize,
      this.state.sortBy.key,
      this.state.sortAsc,
      this.actualStartDate(),
      this.actualEndDate(),
      this.props.params.donorId,
      this.props.serviceAccessToken
    ).then(this.attachViewSparkLabels);
  };

  attachViewSparkLabels = async (
    page: IPaginatedResponse<IDonation>
  ): Promise<IPaginatedResponse<IDonation>> => {
    if (page.result) {
      page.result.forEach(donation => {
        donation.receivedPrettyShort = formatReceivedPrettyShort(
          donation.receivedOn
        );
      });
    }
    const charityId = this.props.currentCharity?.id;
    if (!charityId || !page.result) {
      this.hideEmptyColumns(page.result || []);
      return page;
    }
    if (this.viewSparkLabelCharityId !== charityId) {
      this.viewSparkLabelCache.clear();
      this.viewSparkLabelCharityId = charityId;
    }
    const ids = Array.from(
      new Set(
        page.result.map(trackingPostIdOf).filter(function hasId(id) {
          return id.length > 0;
        })
      )
    );
    await Promise.all(ids.map(id => this.resolveViewSparkLabel(charityId, id)));
    page.result.forEach(donation => {
      const postId = trackingPostIdOf(donation);
      donation.viewSparkPretty = postId
        ? this.viewSparkLabelCache.get(postId) || ""
        : "";
    });
    this.hideEmptyColumns(page.result);
    return page;
  };

  hideEmptyColumns = (rows: IDonation[]) => {
    this.lastDonationRows = rows || [];
    this.setState({ columnNames: this.getColumnNames() });
  };

  resolveViewSparkLabel = async (
    charityId: string,
    postId: string
  ): Promise<string> => {
    const cached = this.viewSparkLabelCache.get(postId);
    if (cached !== undefined) {
      return cached;
    }
    const label = await lookupViewSparkLabel(charityId, postId);
    if (label) {
      this.viewSparkLabelCache.set(postId, label);
    }
    return label;
  };

  selectAllCheck = e => {
    this.setState({
      selectAll: e.target.checked,
      selectedIds: [],
      suppressedIds: [],
      selectedData: [],
    });
  };

  handleColumnHeaderSort = (accessor: string) => {
    const option = this.sortOptions.find(o => o.colName === accessor);
    if (!option) {
      return;
    }
    this.setState(
      prev => {
        const same = prev.sortBy.colName === option.colName;
        return {
          sortBy: option,
          sortAsc: same ? !prev.sortAsc : false,
        };
      },
      () => this.props.refreshDashboard()
    );
  };

  startDateChange = e => {
    let startDate = e;
    let endDate = this.state.endDate;
    if (startDate && startDate.isAfter(endDate)) {
      endDate = startDate;
    }
    this.setState({ startDate, endDate, selectAll: false });
  };

  endDateChange = e => {
    let startDate = this.state.startDate;
    let endDate: Moment = e;
    if (startDate && startDate.isAfter(endDate)) {
      endDate = startDate;
    }
    this.setState({ startDate, endDate, selectAll: false });
  };

  showContactDetails = (donation: IDonation) => {
    if (isDonorDeactivated(donation.donor)) {
      return;
    }
    this.props.navigate(`/contacts/${donation.donor.id}`);
  };

  actualStartDate() {
    if (this.state.startDate) {
      const startDate = moment(this.state.startDate).startOf("day");
      return startDate.toDate().getTime();
    } else {
      return null;
    }
  }

  actualEndDate() {
    if (this.state.endDate) {
      const endDate = moment(this.state.endDate)
        .add(1, "day")
        .startOf("day");
      return endDate.toDate().getTime();
    } else {
      return null;
    }
  }

  toggleActionsMenu = () => {
    this.setState({ actionsMenuOpen: !this.state.actionsMenuOpen });
  };

  execDonationReindex = async () => {
    const searchUrl = this.props.config?.searchServiceUrl;
    const charityId = this.props.currentCharity?.id;
    if (!searchUrl || !charityId) {
      return;
    }
    const confirmed = window.confirm(
      "Queue a full donation reindex for this charity?"
    );
    if (!confirmed) {
      return;
    }
    this.setState({ actionsMenuOpen: false });
    try {
      await enqueueReindexOperation(searchUrl, charityId, "donations");
      window.alert("Donation reindex queued.");
    } catch (err) {
      window.alert(err && err.message ? err.message : "Request failed.");
    }
  };

  execExport = () => {
    const exportAmount = this.state.selectAll
      ? this.state.totalResult - this.state.suppressedIds.length
      : this.state.selectedIds.length === 0
      ? this.state.totalResult
      : this.state.selectedIds.length;
    this.props.toggleExportModal(
      ExportType.donations,
      this.state.selectAll ? this.state.suppressedIds : this.state.selectedIds,
      exportAmount.toString(),
      this.actualStartDate(),
      this.actualEndDate(),
      this.state.selectAll || this.state.selectedIds.length === 0,
      this.props.params.donorId
    );
  };

  goToNewViewSpark = async donations => {
    const thanksDonorSegments = [],
      thanksDonationIds = [],
      donorSet = new Set<string>();

    for (const donation of donations) {
      thanksDonationIds.push(donation.donationId);
      if (donorSet.has(donation.donor.id)) return;

      const donor = await searchDonorById(
        this.props.config.adminServiceUrl,
        this.props.currentCharity.id,
        donation.donor.id,
        this.props.serviceAccessToken
      );

      if (!donorHasNotifications(donor) || isDonorDeactivated(donor)) return;

      donorSet.add(donor.id);
      thanksDonorSegments.push({
        label: donor.name + " - " + getActiveMedium(donor),
        value: "D" + donor.id,
      });
    }
    this.props.navigate("/viewsparks/new", {
      state: {
        thanksDonorSegments: thanksDonorSegments,
        thanksDonationIds,
      },
    });
  };

  showGroupThankYouModal = () => {
    this.props.toggleThankYouModal(
      this.state.selectedData,
      PersonalViewSparkType.ThankYou,
      () => this.goToNewViewSpark(this.state.selectedData),
      null,
      false
    );
  };

  showThankYouModal = (donation: IDonation) => {
    this.props.toggleThankYouModal(
      [donation],
      PersonalViewSparkType.ThankYou,
      () => this.goToNewViewSpark([donation]),
      null,
      false
    );
  };

  selectionUpdate = (update: ISelectionUpdate) => {
    this.setState({
      totalResult: update.totalAmount,
      selectedIds: update.selectedIds,
      suppressedIds: update.suppressedIds,
      selectedData: update.selectedData,
    });
  };

  shouldComponentUpdate(_, nextState: State) {
    return (
      (this.state.selectedIds === nextState.selectedIds &&
        this.state.suppressedIds === nextState.suppressedIds) ||
      this.state.selectAll !== nextState.selectAll ||
      this.state.selectedData !== nextState.selectedData
    );
  }

  goBack = () => {
    if (this.props.params.donorId) {
      this.props.navigate(`/contacts/${this.props.params.donorId}`);
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.currentCharity !== this.props.currentCharity) {
      this.viewSparkLabelCache.clear();
      this.viewSparkLabelCharityId = undefined;
      this.refreshDonorsFirebaseProjectId();
    }
    if (!isEqual(this.props.params, prevProps.params)) {
      this.props.refreshDashboard();
    }
    if (this.props.currentCharity) {
      if (isCurrentUserOnlyCollaborator(this.props.currentCharity)) {
        this.props.navigate("/viewsparks");
      }
      if (this.props.customDonationUrl) {
        this.props.history.push("/contacts");
      }
    }

    if (prevProps.currentCharity != this.props.currentCharity) {
      this.setState({ selectAll: false });
    }

    if (prevProps.enableMotivationCode !== this.props.enableMotivationCode) {
      this.setState({ columnData: this.getColumnData() }, () => {
        this.hideEmptyColumns(this.lastDonationRows);
      });
    }
  }

  clearStartDate = () => {
    this.setState({ startDate: null });
  };

  clearEndDate = () => {
    this.setState({ endDate: null });
  };

  disableColumn = index => {
    if (["Project", "Medium"].includes(this.state.columnNames[index]?.key))
      return;
    const copy = toggleSavedColumn(
      TableIds.donations,
      this.state.columnNames,
      index
    );
    this.setState(
      {
        columnNames: copy,
      },
      () => {
        this.props.columnsModalChange(
          this.state.columnNames.filter(
            column => !["Project", "Medium"].includes(column.key)
          )
        );
      }
    );
  };

  changeColumns = () => {
    const columns = this.state.columnNames.filter(
      column => !["Project", "Medium"].includes(column.key)
    );
    this.props.toggleColumnsModal(index => {
      this.disableColumn(
        this.state.columnNames.findIndex(
          column => column.key === columns[index].key
        )
      );
    }, columns);
  };

  openDonationFirebase = (donation: IDonation) => {
    const key = donation.donationId || donation.id;
    if (!key) {
      return;
    }
    const pid =
      this.state.donorsFirebaseProjectId ||
      (firebase.app().options as { projectId?: string }).projectId;
    if (!pid) {
      return;
    }
    window.open(
      donationConsoleUrl(pid, this.props.currentCharity.id, key),
      "_blank",
      "noopener,noreferrer"
    );
  };

  getExtraColumns = () => {
    const base = [
      {
        id: "contact-details",
        hasTooltip: true,
        tooltipText: "Contact Details",
        type: "icon" as const,
        width: 40,
        faIcon: "fa-user-circle",
        className: "customColUsersIcon",
        showFunctions: this.showContactDetails,
        isDeactivated: (donation: IDonation) =>
          isDonorDeactivated(donation.donor),
        deactivatedClassName: "customColUsersIconDeactivated",
      },
      {
        id: "thank-donor",
        hasTooltip: true,
        tooltipTextFunction: elem => {
          return (
            'Create a "Thank you" Viewspark' +
            (get(elem, "original.thankYouLogs", null)
              ? " (One has been sent already)"
              : "")
          );
        },
        type: "functionalIcon" as const,
        width: 40,
        faIconFunction: elem =>
          get(elem, "original.thankYouLogs", null)
            ? "fas fa-envelope-open"
            : "fas fa-envelope",
        className: "customColUsersIcon",
        isDeactivated: elem => get(elem, "thankYouLogs", null) !== null,
        deactivatedClassName: "customColUsersIconDeactivated",
        showFunctions: this.showThankYouModal,
      },
    ];
    if (!this.props.isAdminDebugUi) {
      return base;
    }
    return [
      ...base,
      {
        id: "firebase-donation",
        hasTooltip: true,
        tooltipText: "Open in Firebase (donation)",
        type: "icon" as const,
        width: 40,
        faIcon: "fa-database",
        className: "customColUsersIcon",
        showFunctions: this.openDonationFirebase,
        isDeactivated: (donation: IDonation) =>
          !(donation.donationId || donation.id),
        deactivatedClassName: "customColUsersIconDeactivated",
      },
    ];
  };

  render() {
    const showStripeKeyRotationBanner =
      this.props.needsToUpdateStripeKey &&
      this.props.stripeAccountType !== "connected" &&
      this.props.serviceAccessToken &&
      this.props.config?.adminServiceUrl &&
      this.props.currentCharity?.id;

    return (
      <Fragment>
        {this.props.currentCharity.disableGive && <NotFoundPage />}
        {!this.props.currentCharity.disableGive && (
          <main className={s.main}>
            <div className={s.container}>
              {showStripeKeyRotationBanner && (
                <StripeKeyRotationBanner
                  charityId={this.props.currentCharity.id}
                  adminServiceUrl={this.props.config.adminServiceUrl}
                  serviceAccessToken={this.props.serviceAccessToken}
                />
              )}
              {this.props.params.donorId && (
                <Row>
                  <Col xs={1} className={s.noPadding}>
                    <div onClick={this.goBack} className={s.cursor}>
                      <div className={s.arrowLeftContainer}>
                        <div className={s.arrowLeft} />
                      </div>
                      <Label className={classNames(s.backLabel, s.cursor)}>
                        Back
                      </Label>
                    </div>
                  </Col>
                </Row>
              )}
              <Row className={s.marginBot}>
                <Col xs={12} className={classNames(s.colMargin, s.noPadding)}>
                  <div className={s.donationsToolbarFlex}>
                    <div className={s.toolbarLeft}>
                      <div
                        className={s.inlineDots}
                        onClick={this.changeColumns}
                        onKeyDown={e => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            this.changeColumns();
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        data-tip
                        data-for="donations-column-mgmt"
                        aria-label="Column management"
                      >
                        <i className={"fas fa-columns " + s.dotIcon}>&nbsp;</i>
                      </div>
                      <ReactTooltip
                        id="donations-column-mgmt"
                        effect="solid"
                        place="top"
                      >
                        Column management
                      </ReactTooltip>
                    </div>
                    {!this.props.params.donorId && (
                      <div className={s.toolbarDates}>
                        <div className={s.dateField}>
                          <label className={s.dateLabel} htmlFor="from">
                            From
                          </label>
                          <InputGroup
                            className={classNames(s.noFlex, s.dateInputGroup)}
                          >
                            <InputGroupText className={s.inputGroup}>
                              <i
                                className={"fas fa-calendar " + s.calendarIcon}
                              >
                                &nbsp;
                              </i>
                            </InputGroupText>
                            <DatePicker
                              id="from"
                              selected={this.state.startDate}
                              selectsStart
                              startDate={this.state.startDate}
                              endDate={this.state.endDate}
                              onChange={this.startDateChange}
                              className={s.text}
                              placeholderText={"mm/dd/yyyy"}
                              autoComplete="off"
                            />
                            <InputGroupText className={s.inputGroupRight}>
                              <i
                                className={"fas fa-times " + s.closeIcon}
                                onClick={this.clearStartDate}
                              >
                                &nbsp;
                              </i>
                            </InputGroupText>
                          </InputGroup>
                        </div>
                        <div className={s.dateField}>
                          <label className={s.dateLabel} htmlFor="to">
                            To
                          </label>
                          <InputGroup
                            className={classNames(s.noFlex, s.dateInputGroup)}
                          >
                            <InputGroupText className={s.inputGroup}>
                              <i
                                className={"fas fa-calendar " + s.calendarIcon}
                              >
                                &nbsp;
                              </i>
                            </InputGroupText>
                            <DatePicker
                              id="to"
                              selected={this.state.endDate}
                              selectsEnd
                              startDate={this.state.startDate}
                              endDate={this.state.endDate}
                              onChange={this.endDateChange}
                              className={s.text}
                              placeholderText={"mm/dd/yyyy"}
                              autoComplete="off"
                            />
                            <InputGroupText className={s.inputGroupRight}>
                              <i
                                className={"fas fa-times " + s.closeIcon}
                                onClick={this.clearEndDate}
                              >
                                &nbsp;
                              </i>
                            </InputGroupText>
                          </InputGroup>
                        </div>
                      </div>
                    )}
                    <div className={s.toolbarActions}>
                      {this.state.selectedData.length > 1 && (
                        <Button
                          className={s.button}
                          color={"none"}
                          onClick={this.showGroupThankYouModal}
                        >
                          Thank Donors
                        </Button>
                      )}
                      <Button
                        id="ExportDonationsButton"
                        color="primary"
                        className={s.newPrimaryButton}
                        onClick={this.execExport}
                      >
                        Export
                      </Button>
                      {this.props.isSuperadmin && (
                        <Dropdown
                          isOpen={this.state.actionsMenuOpen}
                          toggle={this.toggleActionsMenu}
                        >
                          <DropdownToggle
                            color="link"
                            className={s.toolbarExtras}
                            aria-label="More actions"
                          >
                            <i className="fa fa-ellipsis-v" />
                          </DropdownToggle>
                          <DropdownMenu right className={s.actionsDropdownMenu}>
                            <DropdownItem onClick={this.execDonationReindex}>
                              Reindex donations
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                {this.props.serviceAccessToken && this.props.config && (
                  <Dashboard
                    tableId={TableIds.donations}
                    renderRowDetails={this.renderDonationDetails}
                    columnNames={this.state.columnNames}
                    columnData={this.state.columnData}
                    extraColumns={this.getExtraColumns()}
                    selectionUpdate={this.selectionUpdate}
                    checkCol
                    selectAllInCheckboxHeader
                    onSelectAllCheckboxChange={this.selectAllCheck}
                    suppressedIds={this.state.suppressedIds}
                    sortBy={this.state.sortBy.colName}
                    selectAll={this.state.selectAll}
                    sortAsc={this.state.sortAsc}
                    columnHeaderSort={{
                      sortAccessor: this.state.sortBy.colName,
                      sortAscending: this.state.sortAsc,
                      sortableAccessors: this.sortOptions.map(o => o.colName),
                      onSort: this.handleColumnHeaderSort,
                    }}
                    from={this.state.startDate}
                    to={this.state.endDate}
                    currentCharity={this.props.currentCharity}
                    filtered={!!this.state.startDate || !!this.state.endDate}
                    footerMessage={"Donations Selected"}
                    noDataText={"No Donations Found"}
                    getResponse={this.getResponse}
                  />
                )}
              </Row>
            </div>
          </main>
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = (state, _props) => {
  const { firebase, user } = state;
  const cid = user.currentCharity.id;
  const config = `data.${cid}.config`;
  const enableMotivationCode = get(
    firebase,
    `${config}.enableMotivationCode`,
    false
  );
  const customDonationUrl = get(firebase, `${config}.customDonationUrl`, null);
  const needsToUpdateStripeKey =
    get(firebase, `${config}.stripe.needsToUpdateStripeKey`, false) === true;
  const stripeAccountType = get(firebase, `${config}.stripe.accountType`, "");
  return {
    enableMotivationCode,
    customDonationUrl,
    needsToUpdateStripeKey,
    stripeAccountType,
  };
};

const mapDispatchToProps = dispatch => ({
  refreshDashboard: () => {
    dispatch(commonActions.refreshDashboard());
  },
});

export default serviceCaller(
  withLoginRedirect(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(
      firebaseConnect((_, store) => {
        const charityId = store.getState().user.currentCharity?.id;
        if (!charityId) {
          return [];
        }
        return [
          `${charityId}/config/enableMotivationCode`,
          `${charityId}/config/customDonationUrl`,
          `${charityId}/config/stripe`,
        ];
      })(withModals(withStyles(s, datePickerS)(DonationsPage)))
    )
  )
);
